import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, FileText, ListFilter, MessageSquare, Search, ShieldCheck, TrendingUp, UserPlus, Video } from 'lucide-react';

const pipelineStages = [
  { label: 'Identify', icon: Search },
  { label: 'Screen', icon: ListFilter },
  { label: 'Interview', icon: Video },
  { label: 'Communicate', icon: MessageSquare },
  { label: 'Offer', icon: FileText },
  { label: 'Join', icon: UserPlus, strong: true },
  { label: 'Settle', icon: ShieldCheck, strong: true },
];

const incomingBars = [
  ['72%', '46%'],
  ['58%', '40%'],
  ['66%', '34%'],
];

function SurgePipeline({ study }) {
  const panelRef = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = panelRef.current;
    if (!node) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <figure
      ref={panelRef}
      className={`gg-surge min-w-0 overflow-hidden rounded-3xl border border-white/10 bg-[hsl(164_24%_10%)] p-4 sm:p-5 ${seen ? 'is-active' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-2 sm:grid sm:grid-cols-[auto_1fr_auto] sm:gap-3">
        <div className="flex h-16 w-[4.75rem] shrink-0 items-center justify-center rounded-xl bg-white px-1">
          <img src={study.assets.logo} alt="" className="h-14 w-auto object-contain" />
        </div>
        <div className="flex sm:justify-center">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(158_40%_72%)]/35 bg-[hsl(158_40%_72%)]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(158_40%_72%)]">
            <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
            Hiring surge
          </p>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto pb-1 [scrollbar-width:thin]">
        <div className="min-w-full">
          <div className="grid grid-cols-[6.5rem_2rem_minmax(0,1fr)] items-stretch gap-2">
            <div className="rounded-xl border border-white/10 bg-[hsl(164_22%_12%)] px-2.5 py-2.5">
              <p className="text-[11px] font-semibold leading-tight text-white">Incoming candidates</p>
              <ul className="mt-2.5 space-y-2" aria-hidden="true">
                {incomingBars.map((widths) => (
                  <li key={widths[0]} className="flex items-center gap-2">
                    <span className="h-6 w-6 shrink-0 rounded-full border border-[hsl(158_40%_72%)]/35 bg-[hsl(158_40%_72%)]/10" />
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="h-1.5 rounded-full bg-white/20" style={{ width: widths[0] }} />
                      <span className="h-1.5 rounded-full bg-white/10" style={{ width: widths[1] }} />
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] font-medium text-[hsl(158_40%_72%)]">+ more</p>
            </div>

            <svg viewBox="0 0 44 160" className="h-full w-full" fill="none" aria-hidden="true">
              <path className="gg-flow-path" pathLength="1" d="M0 52C18 52 16 86 28 86" stroke="hsl(158 42% 68%)" strokeWidth="1.5" />
              <path className="gg-flow-path" pathLength="1" d="M0 86H28" stroke="hsl(158 42% 68%)" strokeWidth="1.5" />
              <path className="gg-flow-path" pathLength="1" d="M0 120C18 120 16 86 28 86" stroke="hsl(158 42% 68%)" strokeWidth="1.5" />
              <circle cx="33" cy="86" r="7" stroke="hsl(158 42% 68%)" strokeWidth="1.5" />
              <path d="M30 86h6M33.2 83.2 36.2 86l-3 2.8" stroke="hsl(158 42% 68%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            <ol className="grid grid-cols-7 gap-1.5">
              {pipelineStages.map((stage) => {
                const Icon = stage.icon;
                return (
                  <li key={stage.label} className="min-w-0">
                    <div className={`gg-stage flex h-full min-h-[6.25rem] flex-col items-center justify-between rounded-xl px-1 py-2.5 ${stage.strong ? 'gg-stage-strong' : ''}`}>
                      <Icon
                        className={`h-4 w-4 ${stage.strong ? 'text-[hsl(152_46%_70%)]' : 'text-[hsl(158_40%_72%)]'}`}
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                      <span className={`whitespace-nowrap text-center text-[11px] font-semibold leading-none ${stage.strong ? 'text-white' : 'text-[hsl(160_12%_82%)]'}`}>
                        {stage.label}
                      </span>
                      {!stage.strong ? (
                        <span className="h-0.5 w-4 rounded-full bg-white/20" aria-hidden="true" />
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="mt-4 grid grid-cols-[6.5rem_2rem_minmax(0,1fr)] gap-2">
            <div />
            <div />
            <div>
              <div className="relative flex items-center">
                <MessageSquare className="relative z-10 h-3.5 w-3.5 shrink-0 text-[hsl(158_40%_72%)]" aria-hidden="true" />
                <div className="relative ml-2 min-w-0 flex-1 pr-4">
                  <span className="absolute left-0 right-3 top-1/2 h-px -translate-y-1/2 bg-[hsl(158_40%_72%)]/50" aria-hidden="true" />
                  <span className="relative grid grid-cols-7">
                    {pipelineStages.map((stage, index) => (
                      <span key={stage.label} className="flex justify-center">
                        <span
                          className={`gg-comm-dot block rounded-full ${stage.strong ? 'h-2 w-2 bg-[hsl(152_46%_64%)]' : 'h-1.5 w-1.5 bg-[hsl(158_40%_72%)]'}`}
                          style={{ animationDelay: `${index * 160}ms` }}
                        />
                      </span>
                    ))}
                  </span>
                  <ArrowRight className="absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[hsl(158_40%_72%)]" aria-hidden="true" />
                </div>
              </div>
              <p className="mt-3 text-xs font-semibold text-white">Continuous candidate communication</p>
              <p className="mt-0.5 text-xs leading-snug text-[hsl(160_12%_74%)]">From first touch to join and settle.</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .gg-surge .gg-stage {
          border: 1px solid hsl(0 0% 100% / 0.1);
          background: hsl(164 22% 13%);
          transition: transform 200ms ease, border-color 200ms ease, background-color 200ms ease;
        }
        .gg-surge .gg-stage-strong {
          border-color: hsl(152 45% 48% / 0.8);
          background: hsl(158 32% 16%);
        }
        .gg-surge .gg-stage:hover {
          transform: translateY(-2px);
          border-color: hsl(158 40% 72% / 0.45);
        }
        .gg-surge .gg-stage-strong:hover {
          border-color: hsl(152 46% 62% / 0.95);
          background: hsl(158 34% 18%);
        }
        .gg-surge .gg-flow-path { stroke-dasharray: 1; stroke-dashoffset: 1; }
        .gg-surge.is-active .gg-flow-path { animation: gg-draw 900ms ease forwards; }
        .gg-surge.is-active .gg-flow-path:nth-child(2) { animation-delay: 90ms; }
        .gg-surge.is-active .gg-flow-path:nth-child(3) { animation-delay: 180ms; }
        .gg-surge.is-active .gg-comm-dot { animation: gg-pulse 2.8s ease-in-out infinite; }
        @keyframes gg-draw { to { stroke-dashoffset: 0; } }
        @keyframes gg-pulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .gg-surge .gg-flow-path { stroke-dashoffset: 0; animation: none; }
          .gg-surge .gg-stage,
          .gg-surge .gg-comm-dot { animation: none; transition: none; }
          .gg-surge .gg-stage:hover { transform: none; }
        }
      `}</style>
    </figure>
  );
}

export default function GrandGamingHero({ study }) {
  return (
    <section className="border-b border-white/10 bg-[hsl(164_33%_7%)] text-[hsl(40_20%_96%)]">
      <div className="mx-auto grid max-w-[1440px] items-start gap-8 px-4 pb-12 pt-6 sm:px-6 lg:grid-cols-[minmax(0,36rem)_minmax(0,1fr)] lg:gap-10 lg:px-8 lg:pb-14 lg:pt-8">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(158_40%_72%)]">
            {study.category}
          </p>
          <p className="mt-4 max-w-full text-4xl font-bold leading-[0.95] tracking-[-0.03em] text-white sm:text-5xl lg:text-6xl">
            {study.customer}
          </p>
          <h1 className="mt-6 max-w-[26rem] text-3xl font-semibold leading-tight tracking-[-0.02em] text-[hsl(40_20%_96%)] sm:text-4xl">
            {study.headline}
          </h1>
          <p className="mt-5 max-w-[26rem] text-base leading-relaxed text-[hsl(160_12%_78%)] sm:text-lg">
            {study.context}
          </p>
        </div>
        <SurgePipeline study={study} />
      </div>
    </section>
  );
}
